import User, { USER_STORAGE_KEY_MAP } from 'shared/storage/user';
import { getSearchWidgetInstance } from 'client/javascript/pages/shared/sections/hero_container/search_widget';
import { TEAMS } from 'shared/rollbar/global_prefixes';
import { logError } from 'client/javascript/util/logger';

export const LOGGING_CONFIG = {
  prefix: '[SearchWidgetCapacityFilterUI]',
  team: TEAMS.MARKETING
};

export const FILTER_NAME = 'capacity';
const DEFAULT_CAPACITY_VALUE = 1;
// image capacity ranges that determine which product image should be active after user click corresponding filter chip
const IMAGE_RANGES = ['1-5', '6-10', '11-16', '17-100+'];
const CAPACITY_LABEL_TO_VALUE_MAP = {
  '21-100': '21',
  '100+': '101'
};

export const CLASSES = {
  MODULE_FILTER_CHIP_ACTIVE: 'capacity-filter-chip--active',
  MODULE_IMAGE_ACTIVE: 'capacity-filter__image--active'
};

export const SELECTORS = {
  MODULE_DROPDOWN: '[data-dropdown-name="capacity"]',
  MODULE_FILTER_CHIP: '.capacity-filter-chip',
  MODULE_IMAGE: '.capacity-filter__image',
  MODULE_FILTER_CHIP_ACTIVE: `.${CLASSES.MODULE_FILTER_CHIP_ACTIVE}`,
  MODULE_IMAGE_ACTIVE: `.${CLASSES.MODULE_IMAGE_ACTIVE}`
};

export function setupCapacityFilter(domNode) {
  try {
    const dropdownDomNode = domNode.querySelector(SELECTORS.MODULE_DROPDOWN);
    const imagesDomNodes = dropdownDomNode.querySelectorAll(SELECTORS.MODULE_IMAGE);
    const capacityDataFromLocalStorage = User.getValue(USER_STORAGE_KEY_MAP.capacity);
    const searchWidgetInstance = getSearchWidgetInstance();

    setupInitialCapacityFilterState(imagesDomNodes, searchWidgetInstance);

    if (capacityDataFromLocalStorage) {
      applyCapacityDataFromLocalStorage({
        dropdownDomNode,
        imagesDomNodes,
        searchWidgetInstance,
        capacityDataFromLocalStorage
      });
    } else {
      setupInitialCapacityFilterState(imagesDomNodes, searchWidgetInstance);
    }

    setupCapacityFilterChips({ dropdownDomNode, imagesDomNodes, searchWidgetInstance });
  } catch (error) {
    logError(`${LOGGING_CONFIG.prefix}[setupCapacityFilter()] failed`, error, {
      team: LOGGING_CONFIG.team
    });
  }
}

function setupCapacityFilterChips({ dropdownDomNode, imagesDomNodes, searchWidgetInstance }) {
  dropdownDomNode.querySelectorAll(SELECTORS.MODULE_FILTER_CHIP).forEach((filterChipDomNode) => {
    filterChipDomNode.addEventListener('click', ({ currentTarget }) => {
      const { capacityRange, mappedCapacityValue } = currentTarget.dataset;
      const commonCapacityValue = currentTarget.textContent;
      const capacityValue = mappedCapacityValue || commonCapacityValue; // we take either normal capacity values (i.e. 1,2,3,4,5,etc.) or mapped ones (i.e. 21 mapped from 21-100, 101 mapped from 100+)

      toggleCapacityFilterChipClasses(currentTarget, dropdownDomNode);
      toggleCapacityFilterImageClasses(imagesDomNodes, getImageIndexByCapacityRange(capacityRange));

      searchWidgetInstance.setFilterValue(FILTER_NAME, commonCapacityValue);
      User.saveValue(USER_STORAGE_KEY_MAP.capacity, commonCapacityValue);
      searchWidgetInstance.setupFiltersItemsTrackingEvent(FILTER_NAME, commonCapacityValue);
      searchWidgetInstance.updateSearchUrl(FILTER_NAME, capacityValue);
    });
  });
}

function toggleCapacityFilterChipClasses(currentFilterChipDomNode, dropdownDomNode) {
  if (!currentFilterChipDomNode.classList.contains(CLASSES.MODULE_FILTER_CHIP_ACTIVE)) {
    dropdownDomNode
      .querySelector(SELECTORS.MODULE_FILTER_CHIP_ACTIVE)
      .classList.remove(CLASSES.MODULE_FILTER_CHIP_ACTIVE);
    currentFilterChipDomNode.classList.add(CLASSES.MODULE_FILTER_CHIP_ACTIVE);
  }
}

function toggleCapacityFilterImageClasses(imagesDomNodes, imageIndex) {
  imagesDomNodes.forEach((imageDomNode) => {
    imageDomNode.classList.remove(CLASSES.MODULE_IMAGE_ACTIVE);
    if (imagesDomNodes[imageIndex]) {
      imagesDomNodes[imageIndex].classList.add(CLASSES.MODULE_IMAGE_ACTIVE);
    }
  });
}

function applyCapacityDataFromLocalStorage({
  dropdownDomNode,
  imagesDomNodes,
  searchWidgetInstance,
  capacityDataFromLocalStorage
}) {
  const activeFilterChipDomNode = getActiveFilterChip(dropdownDomNode, capacityDataFromLocalStorage);
  const { capacityRange: activeFilterChipCapacityRange } = activeFilterChipDomNode.dataset;

  searchWidgetInstance.setFilterValue(FILTER_NAME, activeFilterChipDomNode.textContent);
  User.saveValue(USER_STORAGE_KEY_MAP.capacity, activeFilterChipDomNode.textContent);
  searchWidgetInstance.updateSearchUrl(FILTER_NAME, getCapacityValue(capacityDataFromLocalStorage));

  toggleCapacityFilterImageClasses(imagesDomNodes, getImageIndexByCapacityRange(activeFilterChipCapacityRange));
  toggleCapacityFilterChipClasses(activeFilterChipDomNode, dropdownDomNode);
}

function setupInitialCapacityFilterState(imagesDomNodes, searchWidgetInstance) {
  const [defaultFirstImage] = imagesDomNodes;

  defaultFirstImage.classList.add(CLASSES.MODULE_IMAGE_ACTIVE);
  User.saveValue(USER_STORAGE_KEY_MAP.capacity, DEFAULT_CAPACITY_VALUE);
  searchWidgetInstance.updateSearchUrl(FILTER_NAME, DEFAULT_CAPACITY_VALUE);
}

function getCapacityValue(capacityDataFromLocalStorage) {
  const capacityValueAsString = capacityDataFromLocalStorage.toString();

  return CAPACITY_LABEL_TO_VALUE_MAP[capacityValueAsString] || capacityValueAsString;
}

function getActiveFilterChip(dropdownDomNode, capacityValue) {
  const filterChipDomNodes = Array.from(dropdownDomNode.querySelectorAll(SELECTORS.MODULE_FILTER_CHIP));
  return filterChipDomNodes.find((filterChipDomNode) => {
    const { mappedCapacityValue } = filterChipDomNode.dataset;

    return mappedCapacityValue === capacityValue || filterChipDomNode.textContent === capacityValue.toString();
  });
}

function getImageIndexByCapacityRange(range) {
  return IMAGE_RANGES.findIndex((imageRange) => imageRange === range);
}
